import LogtoClient from "@logto/browser";

// The deployment injects this object before the bundle is loaded. Keeping the
// values out of source and localStorage avoids turning environment settings or
// credentials into a second persisted auth state.
function normalizeConfig(value) {
  if (!value || typeof value !== "object") return null;
  const text = (item) => String(item || "").trim();
  const list = (items) => (Array.isArray(items) ? items.map(text).filter(Boolean) : undefined);
  const organizations = Array.isArray(value.organizations)
    ? value.organizations.map((item) => {
        if (typeof item === "string") {
          const id = text(item);
          return id ? { id, name: id } : null;
        }
        const id = text(item?.id || item?.organizationId);
        const name = text(item?.name || item?.displayName || id);
        return id && name ? { id, name } : null;
      }).filter(Boolean)
    : undefined;
  const endpoint = text(value.endpoint);
  const appId = text(value.appId || value.clientId);
  if (!endpoint || !appId) return null;
  return {
    endpoint,
    appId,
    redirectUri: text(value.redirectUri) || undefined,
    postSignOutRedirectUri: text(value.postSignOutRedirectUri) || undefined,
    organizations,
    resources: list(value.resources),
    scopes: list(value.scopes),
  };
}

const config = normalizeConfig(globalThis.XHS_LOGTO_CONFIG);
let client = null;
let accessToken = "";
let accessTokenMap = "";
let activeOrganizationId = "";
// 会话存储清理：配置就绪时装配成真正删键的实现，未配置时保持空操作。
// 跨标签登出（P40）要在不跳转登录提供方的前提下清掉本标签的会话，
// 所以这里只暴露"清本地会话"这一个动作。
let clearLocalSessionStorage = () => {};
let activeOrganizationStorageKey = "";
let provisionedOrganizationId = "";
let provisionedOrganizationStorageKey = "";
let organizationClaimsLoaded = false;
let organizationClaimIds = [];
let organizationClaimRoles = new Map();
let organizationClaimNames = new Map();

const ORGANIZATION_URN_PREFIX = "urn:logto:organization:";
const AUTH_CHANGED_EVENT = "xhs-spa-auth-changed";

function getBrowserStorage(name) {
  try {
    return globalThis[name] || null;
  } catch (_error) {
    return null;
  }
}

function clearLegacyCredentialStorage(_appId) {
  const prefixes = ["logto:", "logto_cache:"];
  const exactKeys = ["xhs-backend-auth-token-v1"];
  const clearStorage = (storage) => {
    if (!storage) return;
    for (let index = storage.length - 1; index >= 0; index -= 1) {
      const key = storage.key(index);
      if (exactKeys.includes(key) || prefixes.some((prefix) => key?.startsWith(prefix))) {
        storage.removeItem(key);
      }
    }
  };
  for (const storage of [
    getBrowserStorage("localStorage"),
    getBrowserStorage("sessionStorage"),
  ]) {
    try {
      clearStorage(storage);
    } catch (_error) {
      // One storage area can be unavailable while the other remains usable.
    }
  }
}

function clearStaleSessionStorage(appId) {
  const storage = getBrowserStorage("sessionStorage");
  if (!storage) return;
  const currentPrefix = appId ? `xhs-logto:${appId}:` : "";
  try {
    for (let index = storage.length - 1; index >= 0; index -= 1) {
      const key = storage.key(index);
      if (key?.startsWith("xhs-logto:") && (!currentPrefix || !key.startsWith(currentPrefix))) {
        storage.removeItem(key);
      }
    }
  } catch (_error) {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }
}

function configured(value) {
  return value && typeof value === "object" && String(value.endpoint || "").trim() && String(value.appId || value.clientId || "").trim();
}

if (configured(config)) {
  const appId = String(config.appId || config.clientId).trim();
  clearStaleSessionStorage(appId);
  clearLegacyCredentialStorage(appId);
  client = new LogtoClient({
    endpoint: String(config.endpoint).trim(),
    appId,
    resources: Array.isArray(config.resources) ? config.resources : undefined,
    scopes: Array.isArray(config.scopes) ? config.scopes : undefined,
  });
  const namespace = `xhs-logto:${appId}:`;
  const sessionKeys = new Set(["refreshToken", "idToken", "signInSession"]);
  const session = getBrowserStorage("sessionStorage");
  clearLocalSessionStorage = () => {
    for (const key of sessionKeys) {
      try {
        session?.removeItem(namespace + key);
      } catch (_error) {
        // Session cleanup is best effort when browser storage is unavailable.
      }
    }
  };
  activeOrganizationStorageKey = namespace + "activeOrganizationId";
  provisionedOrganizationStorageKey = namespace + "provisionedOrganizationId";
  try {
    activeOrganizationId = session?.getItem(activeOrganizationStorageKey) || "";
    provisionedOrganizationId = session?.getItem(provisionedOrganizationStorageKey) || "";
  } catch (_error) {
    activeOrganizationId = "";
    provisionedOrganizationId = "";
  }
  client.adapter.storage = {
    async getItem(key) {
      if (key === "accessToken") return accessTokenMap || null;
      if (sessionKeys.has(key)) {
        try {
          return session?.getItem(namespace + key) || null;
        } catch (_error) {
          return null;
        }
      }
      return null;
    },
    async setItem(key, value) {
      if (key === "accessToken") {
        accessTokenMap = String(value || "");
        return;
      }
      if (sessionKeys.has(key)) {
        try {
          session?.setItem(namespace + key, String(value || ""));
        } catch (_error) {
          // Session persistence is best effort in privacy-restricted contexts.
        }
      }
    },
    async removeItem(key) {
      if (key === "accessToken") {
        accessTokenMap = "";
        accessToken = "";
        return;
      }
      if (sessionKeys.has(key)) {
        try {
          session?.removeItem(namespace + key);
        } catch (_error) {
          // Session cleanup is best effort when browser storage is unavailable.
        }
      }
    },
  };
} else {
  clearStaleSessionStorage("");
  clearLegacyCredentialStorage("");
}

function setActiveOrganization(value) {
  activeOrganizationId = String(value || "").trim();
  if (!activeOrganizationStorageKey) return;
  const session = getBrowserStorage("sessionStorage");
  if (!session) return;
  try {
    if (activeOrganizationId) session.setItem(activeOrganizationStorageKey, activeOrganizationId);
    else session.removeItem(activeOrganizationStorageKey);
  } catch (_error) {
    // Session state is best effort; the token returned by the SDK remains authoritative.
  }
}

function setProvisionedOrganization(value) {
  provisionedOrganizationId = normalizeOrganizationId(value);
  if (!provisionedOrganizationStorageKey) return;
  const session = getBrowserStorage("sessionStorage");
  if (!session) return;
  try {
    if (provisionedOrganizationId) session.setItem(provisionedOrganizationStorageKey, provisionedOrganizationId);
    else session.removeItem(provisionedOrganizationStorageKey);
  } catch (_error) {
    // Session state is best effort; the provider token remains authoritative.
  }
}

function normalizeOrganizationId(value) {
  const id = String(value || "").trim();
  return id.startsWith(ORGANIZATION_URN_PREFIX) ? id.slice(ORGANIZATION_URN_PREFIX.length) : id;
}

function addOrganizationRole(roleMap, organizationId, role) {
  const id = normalizeOrganizationId(organizationId);
  const name = String(role || "").trim();
  if (!id || !name) return;
  const roles = roleMap.get(id) || new Set();
  roles.add(name);
  roleMap.set(id, roles);
}

function readOrganizationClaims(claims) {
  const ids = new Set();
  const roles = new Map();
  const addId = (value) => {
    const id = normalizeOrganizationId(value?.id || value?.organizationId || value);
    if (id) ids.add(id);
  };
  const organizationsClaim = claims?.organizations;
  if (Array.isArray(organizationsClaim)) {
    organizationsClaim.forEach(addId);
  } else if (organizationsClaim && typeof organizationsClaim === "object") {
    Object.keys(organizationsClaim).forEach(addId);
  }
  const rolesClaim = claims?.organization_roles;
  if (Array.isArray(rolesClaim)) {
    rolesClaim.forEach((entry) => {
      const value = String(entry || "").trim();
      const separator = value.indexOf(":");
      if (separator < 1) return;
      const id = normalizeOrganizationId(value.slice(0, separator));
      const role = value.slice(separator + 1).trim();
      if (!id || !role) return;
      ids.add(id);
      addOrganizationRole(roles, id, role);
    });
  } else if (rolesClaim && typeof rolesClaim === "object") {
    for (const [organizationId, value] of Object.entries(rolesClaim)) {
      const values = Array.isArray(value) ? value : [value];
      for (const role of values) addOrganizationRole(roles, organizationId, role);
      const id = normalizeOrganizationId(organizationId);
      if (id) ids.add(id);
    }
  }
  return {
    ids: [...ids],
    roles,
  };
}

function clearOrganizationClaims() {
  organizationClaimsLoaded = false;
  organizationClaimIds = [];
  organizationClaimRoles = new Map();
  organizationClaimNames = new Map();
}

function configuredOrganizations() {
  if (!Array.isArray(config?.organizations)) return [];
  return config.organizations.map((item) => {
    if (typeof item === "string") {
      const id = normalizeOrganizationId(item);
      return id ? { id, name: id } : null;
    }
    const id = normalizeOrganizationId(item?.id || item?.organizationId);
    const name = String(item?.name || item?.displayName || id || "").trim();
    return id && name ? { id, name } : null;
  }).filter(Boolean);
}

// Organization ids are assigned by the provider and the ID token claims carry no
// names, so the backend hands over the name of the organization it owns. Best
// effort: if this never resolves the switcher still renders, just with the bare
// organization id.
let managedOrganizationNameLoaded = false;

async function loadManagedOrganizationName() {
  if (managedOrganizationNameLoaded || !client) return;
  try {
    const token = await getAccessToken();
    if (!token) return;
    const response = await fetch("/api/config", {
      headers: { authorization: `Bearer ${token}` },
    });
    if (!response.ok) return;
    const payload = await response.json();
    const id = normalizeOrganizationId(payload?.organization?.id);
    const name = String(payload?.organization?.name || "").trim();
    if (!id || !name) return;
    managedOrganizationNameLoaded = true;
    organizationClaimNames.set(id, name);
    dispatchAuthChanged("organization-name");
  } catch (_error) {
    // Best effort only; retried on the next refresh.
  }
}

// ID token 里的组织列表不是可靠的成员资格清单：在邀请被接受之前签发、或者在切组织
// 之前签发的那个 token，都可能不包含账号确实属于的组织。一个组织到底能不能进，只能
// 问签发方——用组织级访问令牌换一次。返回空串表示确实不可用。
async function getOrganizationAccessToken(organizationId) {
  if (!client) return "";
  const id = normalizeOrganizationId(organizationId);
  if (!id) return "";
  const resource = Array.isArray(config.resources) ? config.resources[0] : undefined;
  try {
    return (await client.getAccessToken(resource, id)) || "";
  } catch (_error) {
    return "";
  }
}

// 持久化的组织在刷新后不可用时，这里只记录事实；由 UI 层（session.js）决定怎么告诉
// 用户，因为静默回落会让组织在刷新后无声地变掉（P41）。
let organizationRestoreWarning = "";

async function refreshOrganizations() {
  if (!client) {
    clearOrganizationClaims();
    return configuredOrganizations();
  }
  let authenticated = false;
  try {
    authenticated = await client.isAuthenticated();
  } catch (_error) {
    authenticated = false;
  }
  if (!authenticated) {
    // 认证没就绪时只清内存里的 claims，**不动**持久化的组织选择：SDK 的就绪是异步的，
    // 刷新瞬间的这一次 false 会把用户上次选的租户抹掉，等真正的 claims 到位时已经只剩
    // 默认组织可回落，护栏再也没有机会跑（P41）。登出清理由 signOutLocal 负责。
    clearOrganizationClaims();
    return configuredOrganizations();
  }
  let claims = {};
  try {
    claims = await client.getIdTokenClaims();
  } catch (_error) {
    // A malformed or unavailable claim set must fail closed for organization UI.
  }
  const parsed = readOrganizationClaims(claims);
  organizationClaimIds = parsed.ids.slice();
  organizationClaimRoles = parsed.roles;
  // A newly provisioned personal organization can be absent from the current
  // ID token until Logto issues a fresh token. It has already been verified by
  // getAccessToken(resource, organizationId), so retain it for this session.
  if (provisionedOrganizationId && !organizationClaimIds.includes(provisionedOrganizationId)) {
    organizationClaimIds.push(provisionedOrganizationId);
  }
  if (provisionedOrganizationId && !organizationClaimRoles.has(provisionedOrganizationId)) {
    organizationClaimRoles.set(provisionedOrganizationId, new Set(["Owner"]));
  }
  organizationClaimsLoaded = true;
  const allowed = new Set(organizationClaimIds);
  if (activeOrganizationId && !allowed.has(activeOrganizationId)) {
    // 不要用可能过期的 claims 去清掉用户上次选的租户——那会让人每次刷新都掉回默认
    // 组织。先向签发方确认它到底可不可用，只有确认不可用才回落并留下待报。
    const token = await getOrganizationAccessToken(activeOrganizationId);
    if (token) {
      if (!organizationClaimIds.includes(activeOrganizationId)) organizationClaimIds.push(activeOrganizationId);
      if (!organizationClaimRoles.has(activeOrganizationId)) organizationClaimRoles.set(activeOrganizationId, new Set());
      allowed.add(activeOrganizationId);
    } else {
      organizationRestoreWarning = activeOrganizationId;
      setActiveOrganization("");
    }
  }
  if (!activeOrganizationId && provisionedOrganizationId && allowed.has(provisionedOrganizationId)) {
    setActiveOrganization(provisionedOrganizationId);
  } else if (!activeOrganizationId && organizationClaimIds.length === 1) {
    setActiveOrganization(organizationClaimIds[0]);
  }
  void loadManagedOrganizationName();
  return organizations();
}

// 取走并清空"刷新后组织被判不可用而回落"这条待报，保证只提示一次。
function consumeOrganizationRestoreWarning() {
  const value = organizationRestoreWarning;
  organizationRestoreWarning = "";
  return value;
}

function getOrganizationRoles(organizationId = activeOrganizationId) {
  const id = normalizeOrganizationId(organizationId);
  return id ? [...(organizationClaimRoles.get(id) || [])] : [];
}

function getOrganizationClaims() {
  return {
    loaded: organizationClaimsLoaded,
    organizations: organizationClaimIds.slice(),
    organization_roles: Object.fromEntries([...organizationClaimRoles.entries()].map(([id, roles]) => [id, [...roles]])),
  };
}

function dispatchAuthChanged(reason = "state") {
  if (typeof globalThis.dispatchEvent !== "function") return;
  const EventConstructor = globalThis.CustomEvent;
  if (typeof EventConstructor !== "function") return;
  globalThis.dispatchEvent(new EventConstructor(AUTH_CHANGED_EVENT, {
    detail: {
      authenticated: Boolean(client && organizationClaimsLoaded),
      reason,
      activeOrganizationId,
      organizations: organizations(),
      claims: getOrganizationClaims(),
    },
  }));
}

async function signIn() {
  if (!client) throw new Error("Logto browser client is not configured");
  clearOrganizationClaims();
  setActiveOrganization("");
  setProvisionedOrganization("");
  await client.signIn(String(config.redirectUri || `${location.origin}/callback`).trim());
}

// Prepare the OAuth URL without navigating the host page. The modal embeds
// this same-origin URL and the normal callback/session handling remains in the
// existing SPA auth client.
async function getSignInUrl() {
  if (!client) throw new Error("Logto browser client is not configured");
  let signInUrl = "";
  const navigate = client.adapter.navigate;
  client.adapter.navigate = async (url) => { signInUrl = String(url); };
  try {
    await client.signIn(String(config.redirectUri || `${location.origin}/callback`).trim());
  } finally {
    client.adapter.navigate = navigate;
  }
  if (!signInUrl) throw new Error("认证地址生成失败");
  return signInUrl;
}

async function handleCallback() {
  if (!client) return false;
  if (!(await client.isSignInRedirected(location.href))) return false;
  try {
    await client.handleSignInCallback(location.href);
    await refreshOrganizations();
    dispatchAuthChanged("callback");
    return true;
  } finally {
    // OIDC redirect lands on /callback?code=…; SDK may strip the query first.
    // Always leave /callback even if organization refresh throws, otherwise the
    // shell looks logged-in while the address bar stays stuck on /callback.
    if (location.pathname === "/callback") {
      history.replaceState({}, "", "/");
    }
  }
}

// 最近一次取到的令牌用的是哪个组织。空白（"")＝没带组织作用域——后端对这类令牌一律
// 回 401（它要求 tenant 身份），那与"会话被撤销"是两回事，调用方必须能分开。
let accessTokenOrganizationId = "";

async function getAccessToken() {
  if (!client) return "";
  // Organization access tokens must still target the backend API resource.
  // Calling getOrganizationToken() alone produces Logto's organization
  // indicator as `aud`, which the API validator correctly rejects.
  const resource = Array.isArray(config.resources) ? config.resources[0] : undefined;
  const organizationId = String(activeOrganizationId || "");
  accessToken = organizationId
    ? await client.getAccessToken(resource, organizationId)
    : await client.getAccessToken(resource);
  accessTokenOrganizationId = organizationId;
  return accessToken || "";
}

async function refreshAccessToken() {
  // Charging-flow refund polling calls this the moment a paid order lands.
  // Without a forced refresh, the SPA keeps a stale access token in memory
  // after returning from the Xunhu payment page; subsequent AI calls still
  // succeed (the AI routes are on the anonymous-allow list) but AuthContext
  // degrades to anonymous and credit deductions are silently skipped, and
  // publish records fall through to the legacy global store. Clearing the
  // SDK's accessToken cache before re-requesting forces a true refresh-token
  // round-trip instead of returning the cached (stale) token.
  if (!client) return "";
  const resource = Array.isArray(config.resources) ? config.resources[0] : undefined;
  const organizationId = String(activeOrganizationId || "");
  try {
    if (organizationId) {
      await client.clearAccessToken(resource, organizationId);
    } else {
      await client.clearAccessToken(resource);
    }
    return await getAccessToken();
  } catch (error) {
    // A failed refresh must NOT block the payment success toast: the operator
    // has already seen "退款成功" / "支付成功" and the next best fallback is
    // to let the next backendFetch go through the anonymous compatibility
    // path. The user will see stale data and can re-login to recover.
    console.warn("[xhsSpaAuth] refreshAccessToken failed; falling back to cached token", error);
    return "";
  }
}

function getAccessTokenOrganizationId() {
  return accessTokenOrganizationId;
}

// Account API base. Locally the dev server proxies the Logto paths on our own
// origin, so the base stays relative; a deployment that serves Logto from its
// own origin gets the absolute one (that origin must allow our CORS).
function accountApiUrl(path) {
  const suffix = `/${String(path || "").replace(/^\/+/, "")}`;
  const endpoint = String(config?.endpoint || "").trim();
  if (!endpoint) return suffix;
  try {
    const origin = new URL(endpoint, location.href).origin;
    return origin === location.origin ? suffix : `${origin}${suffix}`;
  } catch {
    return suffix;
  }
}

// Account API token. Logto answers with an *opaque* user token that carries the
// account scopes, and it only mints that token when no API resource is
// requested: passing a resource (e.g. the Logto API indicator) yields a JWT with
// an empty scope claim, which /api/my-account rejects with 401. Used by the
// self-service settings (display name, password, sessions).
async function getAccountToken() {
  if (!client) return "";
  try {
    return (await client.getAccessToken()) || "";
  } catch (error) {
    console.error("Account API token unavailable", error?.name || "unknown");
    return "";
  }
}

async function isAuthenticated() {
  return Boolean(client && await client.isAuthenticated());
}

// 只清掉本标签的本地会话，不去登录提供方做跳转/登出。跨标签登出（P40）必须用这个：
// 收到"另一个标签已退出"的标签要立刻变成未登录态，但把用户正在看的标签页跳走
// 会把未保存的内容一起丢掉。
async function signOutLocal() {
  accessToken = "";
  accessTokenMap = "";
  clearOrganizationClaims();
  setActiveOrganization("");
  setProvisionedOrganization("");
  clearLocalSessionStorage();
  dispatchAuthChanged("signout");
}

async function signOut() {
  await signOutLocal();
  if (client) await client.signOut(String(config.postSignOutRedirectUri || location.origin).trim());
}

async function switchOrganization(organizationId) {
  if (!client) throw new Error("Logto browser client is not configured");
  const value = normalizeOrganizationId(organizationId);
  if (!value) throw new Error("Organization id is required");
  if (!organizationClaimsLoaded) await refreshOrganizations();
  if (organizationClaimsLoaded && !organizationClaimIds.includes(normalizeOrganizationId(value))) {
    throw new Error("Organization is not present in the authenticated claims");
  }
  const resource = Array.isArray(config.resources) ? config.resources[0] : undefined;
  const token = await client.getAccessToken(resource, value);
  setActiveOrganization(value);
  accessToken = token || "";
  dispatchAuthChanged("organization");
  return accessToken;
}

async function activateOrganization(organizationId, details = {}, { markAsPersonal = false, reason = "organization" } = {}) {
  if (!client) throw new Error("Logto browser client is not configured");
  const value = normalizeOrganizationId(organizationId);
  if (!value) throw new Error("组织初始化未返回组织 ID");
  const resource = Array.isArray(config.resources) ? config.resources[0] : undefined;
  // The token request is the provider-side membership check. Do not expose a
  // newly-created organization in local UI state until Logto accepts it.
  const token = await client.getAccessToken(resource, value);
  if (!token) throw new Error("组织初始化未完成：组织访问令牌为空");
  const roles = Array.isArray(details.roles)
    ? details.roles.map((role) => String(role?.name || role?.roleName || role || "").trim()).filter(Boolean)
    : [];
  organizationClaimsLoaded = true;
  if (!organizationClaimIds.includes(value)) organizationClaimIds.push(value);
  if (roles.length) organizationClaimRoles.set(value, new Set(roles));
  else if (!organizationClaimRoles.has(value)) organizationClaimRoles.set(value, new Set(["Owner"]));
  const name = String(details.name || details.organization?.name || details.organization?.displayName || "").trim();
  if (name) organizationClaimNames.set(value, name);
  if (markAsPersonal) setProvisionedOrganization(value);
  // 个人组织是"默认组织"，不是"切到个人组织"：供给是幂等的、每次加载都会跑一遍，
  // 无条件 setActiveOrganization 会把用户上次选的租户在刷新时顶掉（P41 在 fs03 上
  // 实测到的正是这条路径）。只有还没有选择时才让它顶上——首次登录的初始组织。
  if (!markAsPersonal || !activeOrganizationId) setActiveOrganization(value);
  accessToken = token || "";
  dispatchAuthChanged(reason);
  return token;
}

async function activateProvisionedOrganization(organizationId, details = {}) {
  return activateOrganization(organizationId, details, {
    markAsPersonal: true,
    reason: "provisioned-organization",
  });
}

function getActiveOrganizationId() {
  return activeOrganizationId;
}

async function getUserName() {
  if (!client) return "";
  try {
    const claims = await client.getIdTokenClaims();
    return String(claims?.username || claims?.name || claims?.email || "").trim();
  } catch (error) {
    return "";
  }
}

function organizations() {
  const configured = configuredOrganizations();
  if (!organizationClaimsLoaded) return configured;
  const names = new Map(configured.map((item) => [item.id, item.name]));
  for (const [id, name] of organizationClaimNames.entries()) names.set(id, name);
  return organizationClaimIds.map((id) => ({ id, name: names.get(id) || id, roles: getOrganizationRoles(id) }));
}

globalThis.xhsSpaAuth = Object.freeze({
  configured: Boolean(client),
  organizations,
  refreshOrganizations,
  getOrganizationClaims,
  getOrganizationRoles,
  organizationClaimsLoaded: () => organizationClaimsLoaded,
  isAuthenticated,
  signIn,
  getSignInUrl,
  handleCallback,
  getAccessToken,
  refreshAccessToken,
  getAccessTokenOrganizationId,
  getAccountToken,
  accountApiUrl,
  activateOrganization,
  activateProvisionedOrganization,
  getActiveOrganizationId,
  getUserName,
  getProvisionedOrganizationId: () => provisionedOrganizationId,
  signOut,
  signOutLocal,
  switchOrganization,
  consumeOrganizationRestoreWarning,
});

globalThis.xhsSpaAuthReady = (async () => {
  try {
    const handled = await handleCallback();
    if (!handled && client && await client.isAuthenticated()) await refreshOrganizations();
    return handled;
  } catch (error) {
    accessToken = "";
    clearOrganizationClaims();
    console.error("SPA authentication callback failed", error?.name || "unknown");
    return false;
  }
})();
